import TemporaryTenantClaimEndpoint from '~~/app/marketplace/endpoints/TemporaryTenantClaimEndpoint';

const STORAGE_KEY = 'freemiumTenantClaim';

interface StoredClaim {
    temporaryTenantId: string;
    claimToken: string;
}

// Guards a double fire within the same app load.
let fired = false;

export function useFreemiumTenantClaim() {
    const read = (): StoredClaim | null => {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        try {
            const parsed = JSON.parse(raw);
            if (typeof parsed?.temporaryTenantId !== 'string' || typeof parsed?.claimToken !== 'string') {
                localStorage.removeItem(STORAGE_KEY);
                return null;
            }
            return parsed;
        } catch {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
    };

    const purge = (): void => {
        localStorage.removeItem(STORAGE_KEY);
    };

    // Stores both values atomically as one blob: never a half state.
    const store = (temporaryTenantId: string, claimToken: string): void => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ temporaryTenantId, claimToken }));
    };

    // Fire-and-forget. 2xx/4xx (except 401) purge; 401/5xx/network errors keep for retry on a
    // later app load -- 401 here means the relay itself is misconfigured, not a refusal.
    const fire = (): void => {
        if (fired) return;

        const claim = read();
        if (!claim) return;

        fired = true;

        new TemporaryTenantClaimEndpoint()
            .claim(claim.temporaryTenantId, claim.claimToken)
            .then((response) => {
                const status = response.status;
                if (status >= 200 && status < 300) return purge();
                if (status >= 400 && status < 500 && status !== 401) purge();
            })
            .catch(() => {});
    };

    return { store, fire };
}
